<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { ArrowLeft, Bot, ClipboardCheck, Loader2, MessageSquarePlus, Send, Sparkles } from "lucide-vue-next";
import { v3 } from "@/services/v3";
import { useBriefStore } from "@/stores/briefs";
import MarkdownContent from "@/components/MarkdownContent.vue";

const emit = defineEmits<{ openBrief: [id: number] }>();
const briefs = useBriefStore();
const tab = ref<"agent" | "requests">("agent");
const partner = ref<any>(null);
const sessions = ref<any[]>([]);
const session = ref<any>(null);
const requests = ref<any[]>([]);
const selectedRequestId = ref<number | null>(null);
const agentInput = ref("");
const commentInput = ref("");
const busy = ref(false);
const message = ref("");
const chatPane = ref<HTMLElement | null>(null);
const selectedRequest = computed(() => requests.value.find((item) => item.id === selectedRequestId.value));

const statusLabel: Record<string, string> = {
  draft: "Agent 正在补齐",
  needs_information: "需要补充",
  pending_admin: "等待管理员审核",
  in_progress: "执行中",
  pending_admin_review: "管理员复核中",
  resolved: "已完成",
};
const actionLabel: Record<string, string> = {
  agent_draft: "Agent 建立需求草稿",
  agent_created: "Agent 自动创建工单",
  submitted: "合作伙伴提交工单",
  assign: "管理员审核并分派",
  return: "管理员退回补充",
  submitted_review: "员工提交管理员复核",
  resolve: "管理员复核完成",
  partner_comment: "合作伙伴补充",
  admin_comment: "管理员反馈",
  staff_comment: "执行人员反馈",
};
const quickPrompts = [
  "我想制作一份面向客户介绍蕉绘的产品 PPT",
  "我有一个功能改进建议",
  "系统出现故障，需要帮我创建工单",
  "我需要咨询合同、报价或结算事项",
];

async function load() {
  const [profile, requestResult, sessionResult] = await Promise.all([
    v3.partnerMe(), v3.partnerRequests(), v3.partnerAgentSessions(),
  ]);
  partner.value = profile.partner;
  requests.value = requestResult.requests;
  sessions.value = sessionResult.sessions;
  if (selectedRequestId.value) selectedRequestId.value = requests.value.find((item) => item.id === selectedRequestId.value)?.id || null;
}

async function createSession(requestId?: number) {
  busy.value = true;
  message.value = "";
  try {
    session.value = (await v3.createPartnerAgentSession(requestId ? { requestId, title: "补充工单信息" } : {})).session;
    tab.value = "agent";
    await load();
    await scrollLatest();
  } catch (error: any) {
    message.value = error.message || "无法创建 Agent 会话";
  } finally {
    busy.value = false;
  }
}

async function openSession(id: number) {
  busy.value = true;
  try {
    session.value = (await v3.partnerAgentSession(id)).session;
    await scrollLatest();
  } finally {
    busy.value = false;
  }
}

async function scrollLatest() {
  await nextTick();
  if (chatPane.value) chatPane.value.scrollTop = chatPane.value.scrollHeight;
}

async function sendAgent(content = agentInput.value) {
  const value = content.trim();
  if (value.length < 2 || busy.value) return;
  if (!session.value || session.value.status !== "active") await createSession();
  if (!session.value) return;
  agentInput.value = "";
  session.value.messages.push({ id: -Date.now(), role: "user", content: value, created_at: new Date().toISOString() });
  busy.value = true;
  message.value = "";
  await scrollLatest();
  try {
    const result = await v3.sendPartnerAgentMessage(session.value.id, value);
    session.value = result.session;
    await load();
    await scrollLatest();
    if (result.briefId) {
      await briefs.open(result.briefId);
      await briefs.sendRequirement(result.handoffPrompt || value);
      emit("openBrief", result.briefId);
    }
  } catch (error: any) {
    message.value = error.message || "Agent 暂时没有完成本轮处理";
    session.value = (await v3.partnerAgentSession(session.value.id)).session;
  } finally {
    busy.value = false;
    await scrollLatest();
  }
}

function openRequest(item: any) {
  selectedRequestId.value = Number(item.id);
  tab.value = "requests";
  commentInput.value = "";
}

async function sendComment() {
  if (!selectedRequest.value || commentInput.value.trim().length < 2) return;
  busy.value = true;
  try {
    const result = await v3.commentPartnerRequest(selectedRequest.value.id, commentInput.value);
    commentInput.value = "";
    await load();
    selectedRequestId.value = result.request.id;
  } catch (error: any) {
    message.value = error.message || "反馈发送失败";
  } finally {
    busy.value = false;
  }
}

onMounted(async () => {
  await load();
  const active = sessions.value.find((item) => item.status === "active");
  if (active) await openSession(active.id);
  else await createSession();
});
</script>

<template>
  <div class="partner-page">
    <header class="page-head">
      <div><small>{{ partner?.organization_name }}</small><h1>合作伙伴服务台</h1><p>直接告诉 Agent 你的问题或需求，Agent 会补齐信息、分流并创建工单。</p></div>
      <span class="partner-mark">Agent 模式</span>
    </header>

    <nav class="tabs">
      <button :class="{ active: tab === 'agent' }" @click="tab = 'agent'"><Sparkles :size="17" />Agent 服务</button>
      <button :class="{ active: tab === 'requests' }" @click="tab = 'requests'; selectedRequestId = null"><ClipboardCheck :size="17" />工单与反馈</button>
    </nav>
    <p v-if="message" class="notice">{{ message }}</p>

    <main v-if="tab === 'agent'" class="agent-workspace">
      <aside class="session-list">
        <button class="new-session" :disabled="busy" @click="createSession()"><MessageSquarePlus :size="16" />新需求</button>
        <button v-for="item in sessions" :key="item.id" :class="{ active: session?.id === item.id }" @click="openSession(item.id)">
          <b>{{ item.request_title || item.title }}</b><small>{{ item.status === 'active' ? '沟通中' : item.status === 'ticket_created' ? '已创建工单' : '已转项目' }}</small>
        </button>
      </aside>

      <section class="agent-chat">
        <header><Bot :size="20" /><div><b>合作伙伴 Agent</b><small>产品咨询直接回答，需求完整后自动建单</small></div></header>
        <div ref="chatPane" class="messages">
          <div v-if="!session?.messages?.length" class="welcome">
            <Sparkles :size="24" /><h2>今天需要处理什么？</h2><p>不用先判断类型，也不用填写表格。</p>
            <button v-for="prompt in quickPrompts" :key="prompt" @click="sendAgent(prompt)">{{ prompt }}</button>
          </div>
          <article v-for="item in session?.messages || []" :key="item.id" :class="item.role">
            <small>{{ item.role === 'user' ? '你' : '合作伙伴 Agent' }}</small>
            <MarkdownContent v-if="item.role === 'assistant'" :content="item.content" />
            <p v-else>{{ item.content }}</p>
          </article>
          <div v-if="busy" class="thinking"><Loader2 class="spin" :size="16" />Agent 正在整理、校验并决定下一步</div>
        </div>
        <footer><textarea v-model="agentInput" class="textarea" placeholder="描述你的想法、问题或补充信息…" @keydown.ctrl.enter.prevent="sendAgent()" /><button class="btn btn--primary" :disabled="busy || agentInput.trim().length < 2" @click="sendAgent()"><Send :size="17" />发送</button></footer>
      </section>
    </main>

    <main v-else class="request-workspace" :class="{ detail: selectedRequest }">
      <section class="request-list" :class="{ hidden: selectedRequest }">
        <header><h2>工单与反馈</h2><p>查看管理员审核、分派、执行、复核和最终结果。</p></header>
        <article v-for="item in requests" :key="item.id" @click="openRequest(item)">
          <div><small>{{ item.categoryLabel }}</small><h3>{{ item.title || 'Agent 整理中' }}</h3><p>{{ item.summary || item.details?.desiredOutcome || item.details?.actualBehavior || '等待补充' }}</p></div>
          <aside><span :class="item.status">{{ statusLabel[item.status] || item.status }}</span><b>{{ item.completeness_score }}%</b><small>{{ item.updated_at.slice(0, 16).replace('T', ' ') }}</small></aside>
        </article>
        <p v-if="!requests.length" class="empty">Agent 还没有创建工单。</p>
      </section>

      <section v-if="selectedRequest" class="request-detail">
        <button class="back" @click="selectedRequestId = null"><ArrowLeft :size="17" />返回工单列表</button>
        <header><div><small>工单 #{{ selectedRequest.id }} · {{ selectedRequest.categoryLabel }}</small><h2>{{ selectedRequest.title }}</h2><p>{{ selectedRequest.summary }}</p></div><span>{{ statusLabel[selectedRequest.status] || selectedRequest.status }}</span></header>
        <div class="status-flow"><span class="done">已创建</span><i /><span :class="{ done: ['in_progress','pending_admin_review','resolved'].includes(selectedRequest.status) }">已分派</span><i /><span :class="{ done: ['pending_admin_review','resolved'].includes(selectedRequest.status) }">执行处理</span><i /><span :class="{ done: selectedRequest.status === 'resolved' }">完成复核</span></div>
        <section v-if="selectedRequest.missingFields?.length" class="missing"><b>还需补充</b><span v-for="item in selectedRequest.missingFields" :key="item">{{ item }}</span><button class="btn" @click="createSession(selectedRequest.id)"><Bot :size="15" />继续让 Agent 补齐</button></section>
        <section><h3>完整反馈时间线</h3><ol class="timeline"><li v-for="event in selectedRequest.events" :key="event.id"><i /><div><header><b>{{ actionLabel[event.action] || event.action }}</b><small>{{ event.actor_name }} · {{ event.created_at.slice(0,16).replace('T',' ') }}</small></header><p v-if="event.note">{{ event.note }}</p></div></li></ol></section>
        <section class="comment"><h3>补充反馈</h3><textarea v-model="commentInput" class="textarea" placeholder="补充情况、回复管理员或询问处理进度…" /><button class="btn btn--primary" :disabled="busy || commentInput.trim().length < 2" @click="sendComment"><Send :size="16" />发送反馈</button></section>
      </section>
    </main>
  </div>
</template>

<style scoped>
.partner-page{min-height:calc(100vh - 64px);padding:24px 28px 42px;background:#f5f7f7;color:var(--ink)}.page-head{display:flex;align-items:flex-start;justify-content:space-between;gap:20px;max-width:1180px;margin:0 auto 16px}.page-head small{color:var(--brand-dark);font-weight:700}.page-head h1{margin:3px 0;font-size:26px}.page-head p,.request-list>header p{margin:3px 0;color:var(--muted)}.partner-mark{padding:6px 9px;border:1px solid #c9d9de;background:#fff;color:var(--brand-dark);font-size:12px}.tabs{display:flex;max-width:1180px;margin:0 auto 16px;border-bottom:1px solid var(--line);background:#fff}.tabs button{display:flex;align-items:center;gap:7px;min-height:48px;border:0;border-bottom:3px solid transparent;padding:8px 20px;background:transparent;color:var(--muted)}.tabs button.active{border-bottom-color:var(--brand);color:var(--ink);font-weight:700}.notice{max-width:1180px;margin:0 auto 12px;padding:10px 12px;border-left:3px solid var(--brand);background:#fff8f3;color:var(--brand-dark)}.agent-workspace,.request-workspace{display:grid;grid-template-columns:240px minmax(0,1fr);max-width:1180px;min-height:680px;margin:auto;border:1px solid var(--line);background:#fff}.session-list{border-right:1px solid var(--line);padding:10px}.session-list button{display:grid;width:100%;gap:3px;border:0;padding:10px;background:transparent;text-align:left}.session-list button.active{background:#fff4ed;box-shadow:inset 3px 0 var(--brand)}.session-list small{color:var(--muted)}.session-list .new-session{display:flex;align-items:center;justify-content:center;gap:6px;margin-bottom:10px;border:1px solid var(--line);background:#fff}.agent-chat{display:grid;grid-template-rows:auto minmax(0,1fr) auto;min-width:0;max-height:calc(100vh - 190px)}.agent-chat>header{display:flex;align-items:center;gap:10px;padding:14px 18px;border-bottom:1px solid var(--line)}.agent-chat>header div,.agent-chat>header b,.agent-chat>header small{display:block}.agent-chat>header small{margin-top:2px;color:var(--muted)}.messages{overflow:auto;padding:20px}.messages article{max-width:78%;margin:0 0 16px;padding:13px 15px;line-height:1.65}.messages article small{display:block;margin-bottom:5px;color:var(--muted)}.messages article.user{margin-left:auto;background:#fff1e8}.messages article.assistant{border:1px solid var(--line);background:#fff}.messages article p{margin:0;white-space:pre-wrap}.welcome{display:grid;justify-items:center;max-width:620px;margin:45px auto;gap:8px;text-align:center}.welcome h2,.welcome p{margin:0}.welcome p{color:var(--muted)}.welcome button{width:100%;border:1px solid var(--line);padding:10px;background:#fff;text-align:left}.thinking{display:flex;align-items:center;gap:7px;color:var(--muted)}.agent-chat>footer{display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:end;gap:9px;padding:12px;border-top:1px solid var(--line)}.agent-chat>footer .textarea{min-height:72px}.agent-chat>footer .btn,.comment .btn,.missing .btn{display:flex;align-items:center;gap:6px}.request-workspace{grid-template-columns:1fr}.request-list{padding:20px}.request-list>header{margin-bottom:13px}.request-list h2{margin:0}.request-list article{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:16px;border-bottom:1px solid var(--line);padding:15px 12px;cursor:pointer}.request-list article:hover{background:#fbfcfc}.request-list h3{margin:3px 0}.request-list article p{margin:3px 0;color:var(--muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.request-list aside{display:grid;justify-items:end;gap:3px}.request-list aside span{color:#93611a;font-size:11px}.request-list aside span.resolved{color:var(--success)}.request-list aside small{color:var(--muted)}.request-detail{padding:20px}.request-detail .back{display:flex;align-items:center;gap:5px;border:0;padding:0 0 12px;background:transparent;color:var(--brand-dark)}.request-detail>header{display:flex;justify-content:space-between;gap:16px;padding-bottom:15px;border-bottom:1px solid var(--line)}.request-detail>header h2{margin:4px 0}.request-detail>header p{margin:0;color:var(--muted)}.request-detail>header>span{color:#93611a;font-size:12px}.status-flow{display:grid;grid-template-columns:auto 1fr auto 1fr auto 1fr auto;align-items:center;gap:8px;padding:18px 0}.status-flow i{height:2px;background:#dfe5e6}.status-flow span{color:var(--muted);font-size:11px}.status-flow span.done{color:var(--success);font-weight:700}.request-detail>section{padding:16px 0;border-top:1px solid var(--line)}.request-detail h3{margin:0 0 10px}.missing{display:flex;flex-wrap:wrap;gap:6px;background:#fff8e9}.missing b{width:100%}.missing span{padding:4px 7px;background:#fff;color:#8b5d18;font-size:11px}.missing .btn{margin-left:auto}.timeline{margin:0;padding:0;list-style:none}.timeline li{display:grid;grid-template-columns:14px minmax(0,1fr);gap:10px;position:relative;padding:0 0 18px}.timeline li>i{z-index:1;width:10px;height:10px;margin-top:5px;border:2px solid #fff;background:var(--brand);box-shadow:0 0 0 1px var(--brand)}.timeline li:not(:last-child)::before{content:"";position:absolute;left:4px;top:14px;bottom:0;width:1px;background:var(--line)}.timeline header{display:flex;justify-content:space-between;gap:10px}.timeline small{color:var(--muted)}.timeline p{margin:5px 0 0;white-space:pre-wrap;color:var(--muted)}.comment .textarea{min-height:90px;margin-bottom:8px}.empty{padding:30px;color:var(--muted);text-align:center}.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}
@media(max-width:700px){.partner-page{padding:12px 10px 28px}.page-head{align-items:center}.page-head h1{font-size:21px}.page-head p,.partner-mark{display:none}.tabs{position:sticky;top:56px;z-index:8;margin:0 -10px 10px}.tabs button{flex:1;justify-content:center}.agent-workspace,.request-workspace{grid-template-columns:1fr;min-height:calc(100dvh - 180px);border:0}.session-list{display:flex;gap:5px;overflow-x:auto;border:0;border-bottom:1px solid var(--line);padding:7px}.session-list button{flex:0 0 150px}.session-list .new-session{flex-basis:90px;margin:0}.agent-chat{max-height:calc(100dvh - 185px)}.messages{padding:12px}.messages article{max-width:92%}.welcome{margin:22px auto}.agent-chat>footer{position:sticky;bottom:62px;grid-template-columns:1fr;padding:9px;background:#fff}.agent-chat>footer .btn{justify-content:center}.request-list{padding:10px 4px}.request-list.hidden{display:none}.request-list article{grid-template-columns:1fr}.request-list aside{grid-template-columns:auto auto 1fr;justify-items:start}.request-detail{padding:4px 2px 80px}.request-detail>header{display:grid}.status-flow{grid-template-columns:1fr 1fr;gap:5px}.status-flow i{display:none}.timeline header{display:grid;gap:2px}.comment .btn{width:100%;justify-content:center}}
</style>
